// eslint-disable-next-line import/no-extraneous-dependencies
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';

import { JSX, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { InitCardApiContext } from 'shared/hooks/useInitCardApi';
import { IOfferSchema } from 'shared/repositories/commercial-search-offers/entities/offer/OfferSchema';
import { AdfoxContextData } from 'shared/types/AdfoxContextData';
import { DealType } from 'shared/types/DealType';
import { isLastListItem } from 'shared/utils/array/isLastListItem';
import { memoWithDisplayName } from 'shared/utils/memoWithDisplayName';

import { completeJsonQuerySelector } from '../../store/clientSide/slices/jsonQuery';
import { AdfoxParallaxBannerLoadable } from '../AdfoxParallaxBanner';
import { AdfoxTGBBannerLoadable } from '../AdfoxTGBBanner/AdfoxTGBBannerLoadable';
import { Card } from '../Card';
import { CardContexts } from '../CardContexts';

import { trackOfferCardShow } from './tracking';

import styles from './Cards.css';

type CardsProps = {
  adfox: AdfoxContextData['serverData'];
  dealType: DealType;
  offers: IOfferSchema[];
  initCardApi: InitCardApiContext;
};

export const Cards = memoWithDisplayName<CardsProps>(props => {
  const { dealType, offers, adfox, initCardApi } = props;
  const jsonQuery = useSelector(completeJsonQuerySelector);

  const handleCardTeaserShow = useCallback(
    (index: number, id: number) => {
      trackOfferCardShow({ jsonQuery, offerId: id, position: index + 1 });
    },
    [jsonQuery],
  );

  return (
    <div>
      {offers.reduce((accumulator, offer, index, offersArr) => {
        accumulator.push(
          <CardContexts cardContextData={{ dealType, index, offer }} initCardApi={initCardApi} key={offer.id}>
            <div className={styles['card-wrapper']}>
              <ActionAfterViewObserver
                callback={() => handleCardTeaserShow(index, offer.id)}
                callbackDelay={1000}
                percentVisible={0.5}
                triggerOnce={false}
              >
                <Card />
              </ActionAfterViewObserver>
            </div>
          </CardContexts>,
        );

        if (isLastListItem(offersArr, index)) {
          return accumulator;
        }

        /**
         * @description каждое 10 объявление: 10, 20, 30, ...
         */
        if ((index + 1) % 10 === 0) {
          accumulator.push(
            <div className={styles['adfox-parallax-banner-wrapper']} key={`adfox_parallax_banner_next_to_${offer.id}`}>
              <AdfoxParallaxBannerLoadable adfox={adfox} />
            </div>,
          );
          /**
           * @description каждое 10 объявление если начинать с 5: 5, 15, 25, ...
           */
        } else if ((index + 1) % 10 === 5) {
          accumulator.push(
            <div className={styles['adfox-tgb-banner-wrapper']} key={`adfox_tgb_banner_next_to_${offer.id}`}>
              <AdfoxTGBBannerLoadable adfox={adfox} />
            </div>,
          );
        }

        return accumulator;
      }, Array.of<JSX.Element>())}
    </div>
  );
}, 'Cards');
